import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import siteSetting from '../../config/env';
import { userService } from '../../_services/user.service';
import './PartnerTermsWelcome.css';

/**
 * /welcome/partner-terms — the one-time gate a self-employed professional
 * lands on before using Beu.
 *
 * Why a gate and not a banner: partner terms are the contract between Beu
 * and an independent trader (payments, commission, cancellations, data). An
 * invited stylist gets an email with credentials and lands straight in the
 * diary; the terms were only ever a footer link. This page makes acceptance
 * explicit and auditable (version + timestamp on the server) and re-prompts
 * automatically whenever a new version is published.
 *
 * Kept deliberately minimal — the goal is "read, confirm, get into the app":
 *   1. current published Partner Terms rendered INLINE (not just a link);
 *   2. the confirm button unlocks only once they have scrolled to the end
 *      AND ticked the box, so "I have read" is honest;
 *   3. accept → server records version + timestamp → gate feed cleared →
 *      returned to wherever they were heading.
 *
 * Fail-open everywhere: if the terms can't load, they get a link to the
 * public page and can still confirm; if the accept call fails, they see an
 * error and can retry, but are never trapped (Sign out is always available).
 * See PARTNER-TERMS-GATE.md for the backend contract.
 */

export default function PartnerTermsWelcome() {
  const history = useHistory();
  const location = useLocation();

  const [policy, setPolicy] = useState(null);       // { title, content, version, effectiveDate }
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [termsUnavailable, setTermsUnavailable] = useState(false);

  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [ticked, setTicked] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  const termsRef = useRef(null);
  const firstName = (() => {
    try { return (JSON.parse(localStorage.getItem('user') || '{}').first_name) || ''; } catch (e) { return ''; }
  })();

  // Plain Route, so guard the token ourselves.
  useEffect(() => {
    if (!localStorage.getItem('accessToken')) history.replace('/login');
  }, [history]);

  // Load the current published version (same source as the public page).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${siteSetting.api.v2}policy/partner_terms`)
      .then((res) => {
        if (cancelled) return;
        const doc = res?.data?.responseData;
        if (doc && doc.content) { setPolicy(doc); setLoadingTerms(false); }
        else throw new Error('no published version');
      })
      .catch(() => { if (!cancelled) { setTermsUnavailable(true); setLoadingTerms(false); } });
    return () => { cancelled = true; };
  }, []);

  // "Read to the end": unlock once the scroll box reaches (near) the bottom.
  // If the content is short enough to need no scrolling, unlock immediately.
  const checkScrolled = useCallback(() => {
    const el = termsRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= 24) setScrolledToEnd(true);
  }, []);

  useEffect(() => {
    if (loadingTerms) return undefined;
    // Content may still be laying out; check on next frames + on resize.
    const t1 = setTimeout(checkScrolled, 50);
    const t2 = setTimeout(checkScrolled, 400);
    window.addEventListener('resize', checkScrolled);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener('resize', checkScrolled); };
  }, [loadingTerms, checkScrolled]);

  // If the terms couldn't load at all we can't demand a scroll; the tick alone
  // suffices (and the button links out to the public page).
  const canConfirm = ticked && (scrolledToEnd || termsUnavailable) && !accepting;

  const handleAccept = async () => {
    if (!canConfirm) return;
    setAccepting(true);
    setError('');
    try {
      const res = await userService.acceptPartnerTerms('se_welcome_gate');
      if (res?.data?.statusCode === 0) {
        throw new Error(res?.data?.error?.responseMessage || 'Could not record acceptance');
      }
      // Clear the gate feed so PrivateRoute stops redirecting, then go where
      // they were heading (or the diary).
      try {
        localStorage.setItem('partnerTermsGate', JSON.stringify({ requiresAcceptance: false, currentVersion: policy?.version || null }));
      } catch (e) { /* storage unavailable */ }
      const from = location.state && location.state.from;
      const dest = (from && from.pathname && from.pathname !== '/welcome/partner-terms') ? from.pathname + (from.search || '') : '/diary';
      history.replace(dest);
    } catch (e) {
      console.error('[partner-terms] accept failed:', e);
      setError('We could not save your confirmation. Please check your connection and try again.');
      setAccepting(false);
    }
  };

  const handleSignOut = () => {
    localStorage.clear();
    window.location.replace('/login');
  };

  const effective = policy?.effectiveDate
    ? new Date(policy.effectiveDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="ptw-page">
      <div className="ptw-card">
        <img
          className="ptw-brand"
          src={require('../../assets/images/loginLogo.png')}
          alt="Beu"
        />
        <h1 className="ptw-title">
          {firstName ? `Welcome to Beu, ${firstName}` : 'Welcome to Beu'}
        </h1>
        <p className="ptw-subtitle">
          Before you get started, please read and confirm our Partner Terms of Business.
          They set out how Beu works with you as an independent professional: bookings,
          payments, cancellations and your clients' data.
        </p>

        <div className="ptw-terms-wrap">
          <div className="ptw-terms-header">
            <span className="ptw-terms-name">{policy?.title || 'Partner Terms of Business'}</span>
            {policy?.version && (
              <span className="ptw-terms-version">
                Version {policy.version}{effective ? `, effective ${effective}` : ''}
              </span>
            )}
          </div>

          <div
            className="ptw-terms-body"
            ref={termsRef}
            onScroll={checkScrolled}
            tabIndex={0}
            aria-label="Partner Terms of Business"
          >
            {loadingTerms && <p className="ptw-muted">Loading the terms…</p>}
            {!loadingTerms && policy && <ReactMarkdown>{policy.content}</ReactMarkdown>}
            {!loadingTerms && termsUnavailable && (
              <p className="ptw-muted">
                We couldn't load the terms here just now. You can read them at{' '}
                <a href="/partner-terms" target="_blank" rel="noopener noreferrer">beuapp.com/partner-terms</a>.
              </p>
            )}
          </div>

          {!scrolledToEnd && !termsUnavailable && !loadingTerms && (
            <div className="ptw-scroll-hint" aria-hidden="true">Scroll to read to the end</div>
          )}
        </div>

        <label className={`ptw-tick ${(!scrolledToEnd && !termsUnavailable) ? 'ptw-tick--waiting' : ''}`}>
          <input
            type="checkbox"
            checked={ticked}
            onChange={(e) => setTicked(e.target.checked)}
            disabled={!scrolledToEnd && !termsUnavailable}
          />
          <span>I have read and agree to the Partner Terms of Business.</span>
        </label>

        {error && <div className="ptw-error" role="alert">{error}</div>}

        <button
          type="button"
          className="ptw-confirm"
          onClick={handleAccept}
          disabled={!canConfirm}
        >
          {accepting ? 'Saving…' : 'Confirm and continue'}
        </button>

        <p className="ptw-footnote">
          You can read the terms again at any time from Settings.{' '}
          <button type="button" className="ptw-link" onClick={handleSignOut}>Sign out</button>
        </p>
      </div>
    </div>
  );
}
